import { useEffect, useState } from 'react';

import { createPortal } from 'react-dom';
import { BringgPopover, Menu, Tooltip } from '@bringg/react-components';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { useTranslation } from 'react-i18next';

import {
	canDeletePolygonPoint,
	deletePolygonPoint,
	DeletedPolygonPoint,
	listenToPathChanges,
	PolygonPointTarget
} from 'bringg-web/features/service-area/utils/delete-polygon-point/delete-polygon-point';
import { createVertexAnchor } from 'bringg-web/features/service-area/utils/vertex-anchor/vertex-anchor';

const DELETE_KEY = 'delete';
// the popover sits above antd's default tooltip layer, so the reason would hide under it (as DropdownMenu does)
const TOOLTIP_Z_INDEX = 999999;

interface Props {
	map: google.maps.Map;
	target: PolygonPointTarget;
	onClose: () => void;
	onDeleted: (deleted: DeletedPolygonPoint | null) => void;
}

export function PolygonPointMenu({ map, target, onClose, onDeleted }: Props) {
	const { t } = useTranslation();
	const [anchor, setAnchor] = useState<HTMLDivElement>(null);
	const canDelete = canDeletePolygonPoint(target.polygon);

	useEffect(() => {
		const removeAnchor = createVertexAnchor(map, target.polygon.getPath().getAt(target.vertex), setAnchor);
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				onClose();
			}
		};

		const stopListening = listenToPathChanges(target.polygon, onClose);
		// popper does not follow the map, so a zoom or pan would leave the menu off its point
		const boundsListener = google.maps.event.addListener(map, 'bounds_changed', onClose);

		document.addEventListener('keydown', onKeyDown);

		return () => {
			document.removeEventListener('keydown', onKeyDown);
			stopListening();
			boundsListener.remove();
			removeAnchor();
			setAnchor(null);
		};
	}, [map, target, onClose]);

	if (!anchor) {
		return null;
	}

	// same classes as DropdownMenu, which cannot be opened from code
	const menu = (
		<Menu
			className="bringg-dropdown-menu"
			selectable={false}
			onClick={() => {
				onDeleted(deletePolygonPoint(target));
				onClose();
			}}
			items={[
				{
					key: DELETE_KEY,
					disabled: !canDelete,
					className: 'bringg-dropdown-menu-item',
					icon: <BringgIcon iconName={BringgFontIcons.Trash} />,
					label: (
						<Tooltip
							title={canDelete ? undefined : t('SERVICE_AREA.DELETE_POLYGON_POINT_MIN')}
							placement="right"
							zIndex={TOOLTIP_Z_INDEX}
						>
							<span data-test-id="delete-polygon-point">{t('SERVICE_AREA.DELETE_POLYGON_POINT')}</span>
						</Tooltip>
					)
				}
			]}
		/>
	);

	return createPortal(
		<BringgPopover
			visible
			onVisibleChange={visible => !visible && onClose()}
			content={menu}
			popoverType="dropdown"
			placement="bottom-start"
			overlayClassName="bringg-dropdown-menu-overlay"
			data-test-id="polygon-point-menu"
			noArrow
		>
			<span />
		</BringgPopover>,
		anchor
	);
}
